import { Ionicons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import type { NavigationProp, ParamListBase } from "@react-navigation/native";
import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  TextInput,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  ScrollView,
  Image,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import tw from "twrnc";

import {
  DEMO_GUEST_PROGRESS,
  DEMO_PROFILE_PROGRESS,
  DEMO_USER,
  DEMO_USER_STREAK,
  DEMO_USER_XP,
  getScreenshotPersona,
  isScreenshotMode,
} from "../../../lib/screenshotMode";
import { getCategoryName } from "../../constants/categoryMapping";
import { DSText } from "../../design-system/components/DSText";
import { useAppTheme } from "../../design-system/ThemeProvider";
import { radius, shadows, spacing } from "../../design-system/tokens";
import { useLanguageStore } from "../../features/quiz-core/languageStore";
import { t } from "../../i18n/translations";
import { useGuestUser } from "../contexts/GuestUserContext";
import { supabase } from "../services/supabaseService";

const shellColors = {
  canvas: "#120A2B",
  panel: "#080B24",
  panelSoft: "rgba(14, 18, 48, 0.92)",
  gold: "#F8C84E",
  goldSoft: "rgba(248, 200, 78, 0.18)",
  purple: "#6D35A8",
  ivory: "#FFF7DA",
  muted: "#D9D2C2",
  line: "rgba(248, 200, 78, 0.38)",
};

export async function fetchUserStreak(userId: string) {
  const { data, status } = await supabase
    .from("user_stats")
    .select("current_streak, last_played_at")
    .eq("user_id", userId)
    .single();
  if (status === 406 || !data) {
    return { streak_days: 0, last_played_at: null };
  }
  return { streak_days: data.current_streak || 0, last_played_at: data.last_played_at };
}

export async function fetchUserPoints(userId: string) {
  const { data, status } = await supabase
    .from("user_stats")
    .select("xp")
    .eq("user_id", userId)
    .single();
  if (status === 406 || !data) {
    return { points: 0 };
  }
  return { points: data.xp || 0 };
}

interface UserProgressRow {
  category_id: number;
  difficulty_id: number;
  best_score: number | null;
  total_answered: number | null;
  correct_answered: number | null;
  completed: boolean | null;
  updated_at: string;
}

interface UserProgressItem {
  category_id: number;
  difficulty_id: number;
  best_score: number;
  total_answered: number;
  correct_answered: number;
  completed: boolean;
  updated_at: string;
  accuracy: number;
}

export async function fetchUserProgress(userId: string) {
  const { data, status, error } = await supabase
    .from("user_stats")
    .select("category_id, difficulty_id, best_score, total_answered, correct_answered, completed, updated_at")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false });
  if (status === 406 || !data) {
    return [];
  }
  if (error) throw error;
  return (data as UserProgressRow[]).map((row) => ({
    category_id: row.category_id,
    difficulty_id: row.difficulty_id,
    best_score: row.best_score || 0,
    total_answered: row.total_answered || 0,
    correct_answered: row.correct_answered || 0,
    completed: !!row.completed,
    updated_at: row.updated_at,
    accuracy: row.total_answered > 0 ? Math.round((row.correct_answered / row.total_answered) * 100) : 0,
  }));
}

export default function ProfileScreen() {
  const { colors } = useAppTheme();
  const { language } = useLanguageStore();
  const setGlobalLanguage = useLanguageStore((state) => state.setLanguage);
  const screenshotMode = isScreenshotMode();
  const screenshotGuestMode = screenshotMode && getScreenshotPersona() === "guest";
  const { isGuest, guestProgress, endGuestSession } = useGuestUser();
  const effectiveGuestMode = isGuest || screenshotGuestMode;
  const navigation = useNavigation<NavigationProp<ParamListBase>>();

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [profileLanguage, setProfileLanguage] = useState("en");
  const [loading, setLoading] = useState(true);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [streak, setStreak] = useState<{ streak_days: number; last_played_at: string | null }>({
    streak_days: 0,
    last_played_at: null,
  });
  const [points, setPoints] = useState<number>(0);
  const [progress, setProgress] = useState<UserProgressItem[]>([]);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState("");

  const handleGuestAuthEntry = () => {
    if (effectiveGuestMode) {
      endGuestSession();
    }
  };

  const handleProgressEntry = () => {
    if (effectiveGuestMode) {
      handleGuestAuthEntry();
      return;
    }

    navigation.navigate("GamificationDetail");
  };

  const handleTogetherEntry = () => {
    if (effectiveGuestMode) {
      handleGuestAuthEntry();
      return;
    }

    const parentNavigation = navigation.getParent();

    if (parentNavigation) {
      parentNavigation.navigate("Together");
      return;
    }

    navigation.navigate("Together");
  };

  useEffect(() => {
    const unsubscribe = navigation.addListener("focus", () => {
      fetchAllData();
    });

    async function fetchAllData() {
      setLoading(true);
      setInitialDataLoaded(false);

      if (screenshotMode) {
        if (screenshotGuestMode) {
          const demoGuestProgress = guestProgress || DEMO_GUEST_PROGRESS;
          const mappedGuestProgress = Object.entries(demoGuestProgress.categoryProgress)
            .map(([categoryId, entry]) => ({
              category_id: Number(categoryId),
              difficulty_id: 2,
              best_score: entry.quizzes > 0 ? Math.round(entry.score / entry.quizzes) : 0,
              total_answered: entry.totalQuestions,
              correct_answered: entry.correctAnswers,
              completed: entry.quizzes > 0,
              updated_at: demoGuestProgress.lastPlayedAt,
              accuracy: entry.totalQuestions > 0 ? Math.round((entry.correctAnswers / entry.totalQuestions) * 100) : 0,
            }))
            .sort((a, b) => b.total_answered - a.total_answered);

          setName(t("quickActions.notSignedIn", language) || "Guest Player");
          setProfileLanguage(language || "en");
          setAvatarUrl(null);
          setStreak({ streak_days: 0, last_played_at: demoGuestProgress.lastPlayedAt });
          setPoints(demoGuestProgress.totalScore);
          setProgress(mappedGuestProgress);
        } else {
          setName(DEMO_USER.display_name);
          setProfileLanguage("en");
          setAvatarUrl(DEMO_USER.avatar_url);
          setStreak({
            streak_days: DEMO_USER_STREAK.current_streak,
            last_played_at: DEMO_USER_STREAK.last_activity_date,
          });
          setPoints(DEMO_USER_XP.total_xp);
          setProgress(DEMO_PROFILE_PROGRESS);
        }

        setLoading(false);
        setInitialDataLoaded(true);
        return;
      }

      const user = await supabase.auth.getUser();
      if (!user.data.user) {
        if (effectiveGuestMode && guestProgress) {
          const mappedGuestProgress = Object.entries(guestProgress.categoryProgress)
            .map(([categoryId, entry]) => ({
              category_id: Number(categoryId),
              difficulty_id: 2,
              best_score: entry.quizzes > 0 ? Math.round(entry.score / entry.quizzes) : 0,
              total_answered: entry.totalQuestions,
              correct_answered: entry.correctAnswers,
              completed: entry.quizzes > 0,
              updated_at: guestProgress.lastPlayedAt,
              accuracy: entry.totalQuestions > 0 ? Math.round((entry.correctAnswers / entry.totalQuestions) * 100) : 0,
            }))
            .sort((a, b) => b.total_answered - a.total_answered);

          setName(t("quickActions.notSignedIn", language) || "Guest Player");
          setProfileLanguage(language || "en");
          setAvatarUrl(null);
          setStreak({ streak_days: 0, last_played_at: guestProgress.lastPlayedAt });
          setPoints(guestProgress.totalScore);
          setProgress(mappedGuestProgress);
        }
        setLoading(false);
        setInitialDataLoaded(true);
        return;
      }

      try {
        const [profileResult, streakData, pointsData, progressData] = await Promise.all([
          supabase.from("user_profiles").select("*").eq("id", user.data.user.id).single(),
          fetchUserStreak(user.data.user.id),
          fetchUserPoints(user.data.user.id),
          fetchUserProgress(user.data.user.id),
        ]);

        if (profileResult.data) {
          setName(profileResult.data.name ?? "");
          const dbLanguage = profileResult.data.language;
          setProfileLanguage(dbLanguage ?? language ?? "en");
          if (dbLanguage && dbLanguage !== language) {
            setGlobalLanguage(dbLanguage);
          }
          setAvatarUrl(profileResult.data.avatar_url ?? null);
        }

        setStreak(streakData);
        setPoints(pointsData.points);
        setProgress(progressData);

      } catch (error) {
        console.error("Error fetching profile data:", error);
        setStreak({ streak_days: 0, last_played_at: null });
        setPoints(0);
        setProgress([]);
      } finally {
        setLoading(false);
        setInitialDataLoaded(true);
      }
    }

    fetchAllData();
    return unsubscribe;
  }, [effectiveGuestMode, guestProgress, language, navigation, screenshotGuestMode, screenshotMode, setGlobalLanguage]);

  const startEditingName = () => {
    setTempName(name);
    setIsEditingName(true);
  };

  const saveNameEdit = async () => {
    setName(tempName);
    setIsEditingName(false);

    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("user_profiles")
        .upsert([{ id: user.data.user.id, name: tempName, language: profileLanguage }]);
      if (error) throw error;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t("profile.failedUpdate", language);
      Alert.alert(t("profile.error", language), errorMessage || t("profile.failedUpdate", language));
    }
  };

  const topProgress = useMemo(() => progress.slice(0, 3), [progress]);
  const strongestCategory = topProgress[0];
  const statusLabel = effectiveGuestMode
    ? (t("auth.guestMode", language) || "Guest mode")
    : (t("profile.title", language) || "Profile");

  const actionCards = effectiveGuestMode
    ? [
        {
          key: "sign-in",
          title: t("auth.signIn", language),
          subtitle: t("quickActions.guestNoAccount", language),
          icon: "log-in-outline" as const,
          backgroundColor: colors.primary,
          textColor: colors.onPrimary,
          onPress: () => endGuestSession(),
        },
      ]
    : [
        {
          key: "together",
          title: t("friends_family_play", language),
          subtitle: t("quickActions.myProgressSubtitle", language),
          icon: "people-outline" as const,
          backgroundColor: colors.correct,
          textColor: colors.onCorrect,
          onPress: handleTogetherEntry,
        },
        {
          key: "settings",
          title: t("quickActions.settings", language),
          subtitle: t("quickActions.settingsSubtitle", language),
          icon: "settings-outline" as const,
          backgroundColor: colors.accent,
          textColor: colors.onAccent,
          onPress: () => navigation.navigate("SettingsDetail"),
        },
      ];

  if (!initialDataLoaded || loading) {
    return (
      <SafeAreaView style={[tw`flex-1`, { backgroundColor: shellColors.canvas }]}>
        <View style={tw`flex-1 justify-center items-center px-6`}>
          <View
            style={[
              {
                borderRadius: radius.xl,
                padding: spacing["3xl"],
                alignItems: "center" as const,
                backgroundColor: shellColors.panelSoft,
                borderWidth: 1,
                borderColor: shellColors.line,
                width: "100%",
                maxWidth: 420,
              },
              shadows.lg,
            ]}
          >
            <ActivityIndicator size="large" color={shellColors.gold} style={{ marginBottom: spacing.lg }} />
            <DSText variant="title" color={shellColors.ivory} align="center" style={{ marginBottom: spacing.sm }}>
              {t("profile.loading", language)}
            </DSText>
            <DSText variant="caption" color={shellColors.muted} align="center">
              {t("profile.fetchingStats", language)}
            </DSText>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[tw`flex-1`, { backgroundColor: shellColors.canvas }]} testID="profile-screen">
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingHorizontal: spacing["2xl"],
          paddingTop: spacing.xl,
          paddingBottom: spacing["3xl"],
        }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {effectiveGuestMode && (
          <TouchableOpacity
            activeOpacity={0.92}
            onPress={handleGuestAuthEntry}
          >
            <View
            style={[
              {
                borderRadius: radius.xl,
                padding: spacing.lg,
                marginBottom: spacing["2xl"],
                backgroundColor: shellColors.panelSoft,
                borderWidth: 1,
                borderColor: shellColors.line,
              },
              shadows.sm,
            ]}
          >
            <DSText
              variant="micro"
              uppercase
              color={shellColors.gold}
              style={{ marginBottom: spacing.sm, fontWeight: "700", letterSpacing: 1 }}
            >
              {t("coreUI.local_progress_title", language) || "Local Progress"}
            </DSText>
            <DSText variant="body" color={shellColors.ivory} style={{ marginBottom: spacing.xs }}>
              {t("quickActions.notSignedIn", language) || "Guest Player"}
            </DSText>
              <DSText variant="caption" color={shellColors.muted}>
                {t("quickActions.guestNoAccount", language) ||
                "You are playing as a guest. Sign in to create an account and save your progress."}
              </DSText>
            </View>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          activeOpacity={effectiveGuestMode ? 0.92 : 1}
          disabled={!effectiveGuestMode}
          onPress={handleGuestAuthEntry}
        >
          <View
            style={[
              {
                borderRadius: radius.xl,
                padding: spacing["3xl"],
                marginBottom: spacing["2xl"],
                backgroundColor: shellColors.panel,
                borderWidth: 1,
                borderColor: shellColors.line,
              },
              shadows.lg,
            ]}
          >
          <View style={tw`flex-row items-center`}>
            <View style={{ flex: 1, paddingRight: spacing.lg }}>
              <DSText
                variant="micro"
                uppercase
                color={shellColors.gold}
                style={{ marginBottom: spacing.sm, fontWeight: "700", letterSpacing: 1 }}
              >
                {statusLabel}
              </DSText>

              {isEditingName && !effectiveGuestMode ? (
                <View>
                  <TextInput
                    style={{
                      fontSize: 24,
                      fontWeight: "700",
                      paddingHorizontal: spacing.lg,
                      paddingVertical: spacing.sm,
                      borderRadius: radius.lg,
                      marginBottom: spacing.md,
                      backgroundColor: shellColors.panelSoft,
                      borderWidth: 1,
                      borderColor: shellColors.gold,
                      color: shellColors.ivory,
                    }}
                    value={tempName}
                    onChangeText={setTempName}
                    placeholder={t("profile.usernamePlaceholder", language)}
                    placeholderTextColor={colors.outline}
                    autoFocus
                  />
                  <View style={tw`flex-row gap-3`}>
                    <TouchableOpacity
                      style={[
                        {
                          paddingHorizontal: spacing["2xl"],
                          paddingVertical: spacing.sm,
                          borderRadius: radius.md,
                          backgroundColor: colors.correct,
                        },
                        shadows.sm,
                      ]}
                      onPress={saveNameEdit}
                    >
                      <DSText variant="label" color={colors.onCorrect}>
                        {t("profile.save", language)}
                      </DSText>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        {
                          paddingHorizontal: spacing["2xl"],
                          paddingVertical: spacing.sm,
                          borderRadius: radius.md,
                          backgroundColor: colors.surfaceContainerHighest,
                        },
                        shadows.sm,
                      ]}
                      onPress={() => setIsEditingName(false)}
                    >
                      <DSText variant="label" color={colors.onSurface}>
                        {t("profile.cancel", language)}
                      </DSText>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <TouchableOpacity onPress={effectiveGuestMode ? undefined : startEditingName} disabled={effectiveGuestMode}>
                  <DSText variant="heading" color={shellColors.ivory} style={{ marginBottom: spacing.sm }}>
                    {name || t("profile.tapToSetName", language)}
                  </DSText>
                  <DSText variant="body" color={shellColors.muted} style={{ marginBottom: spacing.sm }}>
                    {effectiveGuestMode
                      ? (t("profile.progressSummary", language) || "Your recent study progress")
                      : (t("quickActions.myProgressSubtitle", language) || "Explore your recent growth, settings, and shared play.")}
                  </DSText>
                  {!effectiveGuestMode && (
                    <DSText variant="caption" color={shellColors.muted}>
                      {t("profile.tapToEdit", language)}
                    </DSText>
                  )}
                  {effectiveGuestMode && (
                    <DSText variant="caption" color={shellColors.gold}>
                      {t("auth.signIn", language) || "Sign In"}
                    </DSText>
                  )}
                </TouchableOpacity>
              )}
            </View>

            <View
              style={{
                borderRadius: radius.full,
                width: 108,
                height: 108,
                backgroundColor: shellColors.goldSoft,
                borderWidth: 1,
                borderColor: shellColors.line,
                alignItems: "center",
                justifyContent: "center",
                shadowColor: shellColors.gold,
                shadowOffset: { width: 0, height: 6 },
                shadowOpacity: 0.28,
                shadowRadius: 12,
                elevation: 10,
              }}
            >
              {avatarUrl ? (
                <Image source={{ uri: avatarUrl }} style={{ width: 108, height: 108, borderRadius: 54 }} />
              ) : (
                  <DSText variant="display" color={shellColors.gold} style={{ fontSize: 42 }}>
                  {name ? name[0].toUpperCase() : "?"}
                </DSText>
              )}
            </View>
          </View>

          <View style={[tw`flex-row gap-3`, { marginTop: spacing["2xl"] }]}>
            <TouchableOpacity
              activeOpacity={0.92}
              onPress={handleProgressEntry}
              style={[
                tw`flex-1`,
                {
                  borderRadius: radius.lg,
                  padding: spacing.lg,
                  backgroundColor: 'rgba(255,247,218,0.08)',
                  borderWidth: 1,
                  borderColor: shellColors.line,
                },
              ]}
            >
              <DSText variant="micro" uppercase color={shellColors.muted} style={{ marginBottom: spacing.xs }}>
                {t("profile.streak", language) || "Streak"}
              </DSText>
              <DSText variant="title" color={shellColors.ivory} style={{ marginBottom: spacing.xs }}>
                {streak?.streak_days ?? 0}
              </DSText>
              <DSText variant="caption" color={shellColors.muted}>
                {t("profile.streak", language) || "Streak"}
              </DSText>
            </TouchableOpacity>
            <TouchableOpacity
              activeOpacity={0.92}
              onPress={handleProgressEntry}
              style={[
                tw`flex-1`,
                {
                  borderRadius: radius.lg,
                  padding: spacing.lg,
                  backgroundColor: 'rgba(255,247,218,0.08)',
                  borderWidth: 1,
                  borderColor: shellColors.line,
                },
              ]}
            >
              <DSText variant="micro" uppercase color={shellColors.muted} style={{ marginBottom: spacing.xs }}>
                {t("profile.xp", language)}
              </DSText>
              <DSText variant="title" color={shellColors.ivory} style={{ marginBottom: spacing.xs }}>
                {points}
              </DSText>
              <DSText variant="caption" color={shellColors.muted}>
                {strongestCategory
                  ? getCategoryName(strongestCategory.category_id, language)
                  : (t("gamification.your_progress", language) || "Your progress")}
              </DSText>
            </TouchableOpacity>
          </View>
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          activeOpacity={0.92}
          onPress={handleProgressEntry}
        >
          <View
            style={[
              {
                borderRadius: radius.xl,
                padding: spacing["2xl"],
                marginBottom: spacing["2xl"],
                backgroundColor: shellColors.panelSoft,
                borderWidth: 1,
                borderColor: shellColors.line,
              },
              shadows.md,
            ]}
          >
          <View style={tw`flex-row items-start justify-between`}>
            <View style={{ flex: 1, paddingRight: spacing.lg }}>
              <DSText
                variant="micro"
                uppercase
                color={shellColors.gold}
                style={{ marginBottom: spacing.sm, fontWeight: "700", letterSpacing: 1 }}
              >
                {t("gamification.your_progress", language) || "Your progress"}
              </DSText>
              <DSText variant="title" color={shellColors.ivory} style={{ marginBottom: spacing.sm }}>
                {strongestCategory
                  ? getCategoryName(strongestCategory.category_id, language)
                  : (t("profile.quizHistory", language) || "Quiz history")}
              </DSText>
              <DSText variant="caption" color={shellColors.muted}>
                {strongestCategory
                  ? t("profile.questionsAnsweredSummary", language)
                      .replace("{{answered}}", String(strongestCategory.correct_answered))
                      .replace("{{total}}", String(strongestCategory.total_answered))
                  : (t("profile.noProgressYet", language) || "No progress yet")}
              </DSText>
            </View>
            {strongestCategory && (
              <View
                style={{
                  borderRadius: radius.full,
                  paddingHorizontal: spacing.md,
                  paddingVertical: spacing.xs,
                  backgroundColor: shellColors.goldSoft,
                }}
              >
                <DSText variant="micro" color={shellColors.gold} uppercase style={{ fontWeight: "700" }}>
                  {strongestCategory.accuracy}%
                </DSText>
              </View>
            )}
          </View>

          <View style={{ marginTop: spacing.lg }}>
            {topProgress.length > 0 ? (
              topProgress.map((entry, index) => (
                <View
                  key={`${entry.category_id}-${entry.difficulty_id}`}
                  style={{
                    paddingVertical: spacing.md,
                    borderTopWidth: index === 0 ? 0 : 1,
                    borderTopColor: shellColors.line,
                  }}
                >
                  <View style={tw`flex-row items-center justify-between`}>
                    <View style={{ flex: 1, paddingRight: spacing.md }}>
                      <DSText variant="label" color={shellColors.ivory} style={{ marginBottom: spacing.xs }}>
                        {getCategoryName(entry.category_id, language)}
                      </DSText>
                      <DSText variant="caption" color={shellColors.muted}>
                        {t("profile.bestScoreSummary", language).replace("{{score}}", String(entry.best_score))}
                      </DSText>
                    </View>
                    <DSText variant="label" color={shellColors.gold}>
                      {entry.accuracy}%
                    </DSText>
                  </View>
                </View>
              ))
            ) : (
              <View
                style={{
                  borderRadius: radius.md,
                  padding: spacing.lg,
                  marginTop: spacing.md,
                  backgroundColor: 'rgba(255,247,218,0.08)',
                  borderWidth: 1,
                  borderColor: shellColors.line,
                }}
              >
                <DSText variant="body" color={shellColors.muted}>
                  {t("profile.noProgressYet", language)}
                </DSText>
              </View>
            )}
          </View>
          </View>
        </TouchableOpacity>

        <View
          style={[
            {
              borderRadius: radius.xl,
              padding: spacing["2xl"],
              marginBottom: spacing["2xl"],
              backgroundColor: shellColors.panelSoft,
              borderWidth: 1,
              borderColor: shellColors.line,
            },
            shadows.md,
          ]}
        >
          <DSText
            variant="micro"
            uppercase
            color={shellColors.gold}
            style={{ marginBottom: spacing.sm, fontWeight: "700", letterSpacing: 1 }}
          >
            {t("quickActions.title", language)}
          </DSText>
          <DSText variant="title" color={shellColors.ivory} style={{ marginBottom: spacing.sm }}>
            {effectiveGuestMode ? (t("auth.signIn", language) || "Sign in") : (t("settings.title", language) || "Settings")}
          </DSText>
          <DSText variant="caption" color={shellColors.muted} style={{ marginBottom: spacing.lg }}>
            {effectiveGuestMode
              ? (t("quickActions.guestNoAccount", language) || "Save your progress and unlock Premium sharing.")
              : (t("quickActions.settingsSubtitle", language) || "Move between your shared play space, settings, and account details.")}
          </DSText>

          {actionCards.map((actionCard, index) => (
            <TouchableOpacity
              key={actionCard.key}
              style={[
                tw`flex-row items-center justify-between`,
                {
                  marginTop: index === 0 ? 0 : spacing.md,
                  padding: spacing.lg,
                  borderRadius: radius.lg,
                  backgroundColor: actionCard.backgroundColor,
                },
                shadows.sm,
              ]}
              onPress={actionCard.onPress}
            >
              <View style={{ flex: 1, paddingRight: spacing.md }}>
                <DSText variant="label" color={actionCard.textColor} style={{ marginBottom: spacing.xs }}>
                  {actionCard.title}
                </DSText>
                <DSText variant="caption" color={actionCard.textColor}>
                  {actionCard.subtitle}
                </DSText>
              </View>
              <Ionicons name={actionCard.icon} size={18} color={actionCard.textColor} />
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
